import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query'
import { isDefined, Logger, messageFromError, type APIError, type ResponseSuccess, type UserId } from '@terros/common'
import { useCallback, useMemo } from 'react'
import { useServerRequest } from './api'
import { type GetHookResponse } from './hooks'

const logger = new Logger('useTantalimQuery')
// logger.level = 'verbose'

export type TantalimQueryOptions<Data> = Omit<UseQueryOptions<Data, APIError>, 'queryKey'> & {
  impersonateUserId?: UserId
}
export type TinyQueryOptions = {
  enabled?: boolean
  staleTime?: number
  gcTime?: number
  refetchInterval?: number
  refetchIntervalInBackground?: boolean
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
}

type QueryInputs<Input, Success, Data> = {
  url: string
  queryKey: QueryKey
  input: Input
  convertResponse: (success: Success) => Data
  options?: TantalimQueryOptions<Data>
}

export function useTantalimQuery<ApiInput, ApiSuccess extends ResponseSuccess, Data = ResponseSuccess>({
  url,
  queryKey,
  input,
  options,
  convertResponse,
}: QueryInputs<ApiInput, ApiSuccess, Data>): GetHookResponse<Data> {
  const impersonateUserId = options?.impersonateUserId
  const serverRequest = useServerRequest<ApiInput, ApiSuccess>(url, impersonateUserId)
  const queryFn = useCallback(async (): Promise<Data> => {
    const result = await serverRequest(input)
    return convertResponse(result)
  }, [input, serverRequest, convertResponse])

  const resolvedQueryKey = isDefined(impersonateUserId) ? [...queryKey, impersonateUserId] : queryKey
  const query = useQuery<Data, APIError>({
    queryFn,
    retry: false,
    ...options,
    queryKey: resolvedQueryKey,
  })

  const lastUpdatedAt = useMemo(() => {
    if (query.isError) return query.errorUpdatedAt
    if (query.isSuccess) return query.dataUpdatedAt
    return undefined
  }, [query])

  logger.verbose(
    `render useTantalimQuery: url=${url} key=${resolvedQueryKey} loading=${query.isLoading}, fetching=${query.isFetching}, success=${query.isSuccess}`
  )

  const error = messageFromError(query.error)
  return { ...query, error, lastUpdatedAt }
}
